// SEQ-240: "How is this worked out" text for the Dashboard's Financial P&L
// Overview: the four cards, the four charts and the Pareto analysis with its
// Range control and table columns. Edit this file whenever those
// calculations change.
//
// WHAT IT DESCRIBES (line numbers at commit 3eed3d4):
// - useDashboardPnl.ts 305-309: active projects only (status active or blank).
// - useDashboardPnl.ts 104-124: claim reports for income and retention;
//   claims_pnl_monthly_values with source = 'manual' only (Xero never counts);
//   project_financial_settings, materials percent 30 and wages multiplier
//   1.04 when unset (126-153).
// - useDashboardPnl.ts 161-284: per project, income by claim month through
//   deriveConstructionIncomeExGst (claimFinancials.ts 260-273), one month at a
//   time through calculateMonthlyPnl (pnlCalculationCore.ts 115-213, wages as
//   typed) and a total through calculateTotalsPnl (219-332, Wages and Extra
//   wages times the multiplier). The card's retention is open retention:
//   claims_reports.retention_amount minus retention_received_amount, summed
//   per project by openRetention (dashboardPnlRetention.ts), not clamped.
// - useDashboardPnl.ts 333-336: projects with no income and no costs dropped.
// - useDashboardPnl.ts 348-396 and 411-415: the monthly trend, the cost split
//   and the four card totals.
// - PnlDashboardSection.tsx 176-283: with any filter set, narrowed to the
//   filtered projects; with a date filter, income, costs and profit are added
//   up from the monthly results (so wages are not multiplied), retention
//   stays all time, and the cost split keeps its all-time shares scaled to
//   the filtered total.
// - PnlDashboardCharts.tsx: ProjectProfitChart (top 8 by net profit, all
//   time), CostBreakdownTreemap, ProfitMarginRadial (top 6 with income, all
//   time), ParetoAnalysisChart (the Range window counted back from this month,
//   calculateTotalsPnl with a fixed 1.04 multiplier, values above zero only,
//   Vital while the running share is 80% or less, plus the first project).
//
// dashboardFigureDefinitions.test.ts checks every id here is used by a
// component and every id a component uses is here.

import type { MetricDefinition } from "../components/howWorkedOut/metricDefinition";

const INCOME_LINES = [
  "Construction income: each progress claim's subtotal, before GST and before retention is held back. An older claim with no subtotal counts its total ex GST plus its retention.",
  "Income counts in the month of the claim, whether or not it has been paid.",
];

const COST_LINES = [
  "Materials budget: income times the project's materials percentage (30% when not set). The typed Materials row is not added; the budget replaces it.",
  "Plus every typed cost marked to come off profit, including a cost whose category is not set up.",
  "Plus wages: Wages and Extra wages times the project's wages multiplier (1.04 unless set otherwise), and contractors, stripping and other labour rows as typed.",
];

const DATE_FILTER_WAGES_LINE =
  "With a date filter, the months are added up as typed, so wages are not multiplied and the total can be lower than the all-time figure.";

const FROM_CLAIMS_AND_TYPED =
  "Income from the progress claims; costs typed in on each project's P&L tab. Only typed rows count; Xero is not counted.";

const DROPPED_LINE = "Projects with no income and no costs are left out.";

const PARETO_WINDOW_DETAIL =
  "The last N months, set by the Range control, counting this month (1 to 60, starts at 6). The date filter does not apply.";

const PARETO_SHARED_LINES = [
  "Keeps only projects whose value is above zero and ranks them largest first.",
  "Vital: the projects at the top whose running share stays at 80% or less, plus always the first one. Trivial: the rest.",
];

const PARETO_FIXED_MULTIPLIER_LINE =
  "Wages and Extra wages are multiplied by 1.04 for every project here. A project's own wages multiplier setting is not read by this chart.";

export const PNL_DASHBOARD_FIGURES = {
  "pnl.total-income": {
    name: "Total income",
    meaning: "The value of work claimed on the projects in view, before GST and before retention is held back.",
    formula: ["Adds up construction income for every project.", ...INCOME_LINES],
    source: { kind: "calculated", detail: "From the progress claims (claim reports). Xero is not counted." },
    projects: { scope: "active-only", detail: DROPPED_LINE },
    dates: { scope: "date-filter", detail: "With the date filter on All time, it covers all time." },
    period: "All time, or the months in the date filter.",
  },
  "pnl.total-costs": {
    name: "Total costs",
    meaning: "Everything taken off income to reach profit, across the projects in view.",
    formula: [...COST_LINES, DATE_FILTER_WAGES_LINE],
    source: { kind: "mixed", detail: FROM_CLAIMS_AND_TYPED },
    projects: { scope: "active-only", detail: DROPPED_LINE },
    dates: { scope: "date-filter", detail: "With the date filter on All time, it covers all time." },
    period: "All time, or the months in the date filter.",
  },
  "pnl.net-profit": {
    name: "Net profit",
    meaning: "What is left of income after every cost, and that as a share of income.",
    formula: [
      "Total income minus total costs.",
      "Margin: net profit divided by total income, times 100.",
      DATE_FILTER_WAGES_LINE,
    ],
    source: { kind: "mixed", detail: FROM_CLAIMS_AND_TYPED },
    projects: { scope: "active-only", detail: DROPPED_LINE },
    dates: { scope: "date-filter", detail: "With the date filter on All time, it covers all time." },
    period: "All time, or the months in the date filter.",
  },
  "pnl.retention-held": {
    name: "Held less received",
    meaning: "The retention still held on the projects in view: held back on their progress claims, less what has been paid back.",
    formula: [
      "Adds up the retention held back on every progress claim, then takes off the retention already received back.",
      "The Retention section's figures can differ: they use the retention from a claim's payment reconciliation when there is one.",
    ],
    source: { kind: "calculated", detail: "From the progress claims (claim reports)." },
    projects: { scope: "active-only", detail: DROPPED_LINE },
    dates: { scope: "all-time" },
    period: "Every claim ever made, up to today.",
  },
  "pnl.monthly-trend": {
    name: "Monthly P&L trend",
    meaning: "Income, costs and net profit, month by month.",
    formula: [
      "For each month: income from that month's claims, costs typed for that month, and income minus costs.",
      "Each project counts every month from its first claim or cost to its last, even a month with nothing in it.",
      "Months are added up as typed, so wages are not multiplied here. Twelve months will not add up to the all-time cards.",
    ],
    source: { kind: "mixed", detail: FROM_CLAIMS_AND_TYPED },
    projects: { scope: "active-only", detail: DROPPED_LINE },
    dates: { scope: "date-filter" },
    period: "One point per month.",
  },
  "pnl.project-profit": {
    name: "Project profit comparison",
    meaning: "The eight most profitable projects, with any loss shown in red.",
    formula: [
      "Net profit for each project: income minus costs, all time.",
      ...COST_LINES,
      "Ranks projects by net profit and shows the top eight. Click a bar to open that project.",
    ],
    source: { kind: "mixed", detail: FROM_CLAIMS_AND_TYPED },
    projects: { scope: "active-only", detail: DROPPED_LINE },
    dates: { scope: "all-time" },
    period: "All time, up to today.",
  },
  "pnl.cost-distribution": {
    name: "Cost distribution",
    meaning: "How total costs split into materials, wages and other costs.",
    formula: [
      "Materials: the materials budget plus typed engineering, handrails and stairs, and misc materials.",
      "Wages: Wages and Extra wages times the wages multiplier, plus contractors, stripping and other labour rows.",
      "Other: every other typed cost marked to come off profit, such as core costs, patching, yard and office.",
      "Each share is that group divided by the total, times 100.",
      "With a date filter, the all-time shares are kept and scaled to the filtered total cost.",
    ],
    source: { kind: "mixed", detail: FROM_CLAIMS_AND_TYPED },
    projects: { scope: "active-only", detail: DROPPED_LINE },
    dates: { scope: "date-filter", detail: "The shares themselves are always all time." },
    period: "All time, or the months in the date filter.",
  },
  "pnl.profit-margins": {
    name: "Profit margins by project",
    meaning: "The six projects with the highest profit margin.",
    formula: [
      "Margin for each project: net profit divided by income, times 100, all time.",
      "Only projects with income count. Ranks them by margin and shows the top six.",
      "Each arc is drawn between 0% and 100%; the label beside it shows the real margin.",
    ],
    source: { kind: "mixed", detail: FROM_CLAIMS_AND_TYPED },
    projects: { scope: "active-only", detail: DROPPED_LINE },
    dates: { scope: "all-time" },
    period: "All time, up to today.",
  },
  "pnl.pareto.cost": {
    name: "Pareto analysis: cost",
    meaning: "Which few projects make up most of the cost in the Range window.",
    formula: [
      "Cost for each project over the window: materials budget, typed costs that come off profit, and wages.",
      PARETO_FIXED_MULTIPLIER_LINE,
      ...PARETO_SHARED_LINES,
    ],
    source: { kind: "mixed", detail: FROM_CLAIMS_AND_TYPED },
    projects: { scope: "active-only", detail: DROPPED_LINE },
    dates: { scope: "own-window", detail: PARETO_WINDOW_DETAIL },
    period: "The last N months set by Range, one table column per month.",
  },
  "pnl.pareto.income": {
    name: "Pareto analysis: income",
    meaning: "Which few projects bring in most of the income in the Range window.",
    formula: ["Income for each project over the window, from its progress claims.", ...INCOME_LINES, ...PARETO_SHARED_LINES],
    source: { kind: "calculated", detail: "From the progress claims (claim reports). Xero is not counted." },
    projects: { scope: "active-only", detail: DROPPED_LINE },
    dates: { scope: "own-window", detail: PARETO_WINDOW_DETAIL },
    period: "The last N months set by Range, one table column per month.",
  },
  "pnl.pareto.profit": {
    name: "Pareto analysis: profit",
    meaning: "Which few projects make most of the profit in the Range window.",
    formula: [
      "Net profit for each project over the window: income minus costs.",
      PARETO_FIXED_MULTIPLIER_LINE,
      "A project that made a loss in the window is left out of this view.",
      ...PARETO_SHARED_LINES,
    ],
    source: { kind: "mixed", detail: FROM_CLAIMS_AND_TYPED },
    projects: { scope: "active-only", detail: DROPPED_LINE },
    dates: { scope: "own-window", detail: PARETO_WINDOW_DETAIL },
    period: "The last N months set by Range, one table column per month.",
  },
  "pnl.pareto.patching": {
    name: "Pareto analysis: patching",
    meaning: "Which few projects carry most of the patching (rework) cost in the Range window.",
    formula: [
      "Patching for each project over the window: the typed Patching cost row.",
      "% Inc beside each figure: patching divided by income, times 100. 1.5% or less shows green, above 1.5% up to 3% orange, above 3% red.",
      ...PARETO_SHARED_LINES,
      "In Safe Mode this is the only view, and its dollar figures stay visible on purpose.",
    ],
    source: { kind: "typed", detail: "Patching on each project's P&L tab; income from the progress claims. Xero is not counted." },
    projects: { scope: "active-only", detail: DROPPED_LINE },
    dates: { scope: "own-window", detail: PARETO_WINDOW_DETAIL },
    period: "The last N months set by Range, one table column per month.",
  },
  "pnl.pareto.range": {
    name: "Pareto range",
    meaning: "How many months the Pareto chart and table count.",
    formula: [
      "Minus and plus change the number of months by one, from 1 to 60. It starts at 6.",
      "The window always ends with this month and counts back. It does not move the window to earlier dates.",
    ],
    source: { kind: "calculated", detail: "A setting on this chart only. It is not saved and resets when the page reloads." },
    projects: { scope: "active-only" },
    dates: { scope: "own-window", detail: "The date filter does not change it." },
    period: "The last N months, set by the Range control, counting this month.",
  },
  "pnl.pareto.col.total": {
    name: "Total",
    meaning: "The chosen measure for this project, added up over the Range months.",
    formula: ["Adds the project's value for every month in the window.", "Hover a cell for the full dollar figure and its share of income."],
    source: { kind: "mixed", detail: FROM_CLAIMS_AND_TYPED },
    projects: { scope: "active-only" },
    dates: { scope: "own-window", detail: PARETO_WINDOW_DETAIL },
    period: "The last N months set by Range.",
  },
  "pnl.pareto.col.defect-ratio": {
    name: "% Inc (defect ratio)",
    meaning: "Patching cost as a share of the project's income over the Range months.",
    formula: [
      "Patching divided by income, times 100.",
      "1.5% or less shows green, above 1.5% up to 3% orange, above 3% red.",
      "Shows 0% when the project has no income in the window.",
    ],
    source: { kind: "mixed", detail: "Patching on each project's P&L tab; income from the progress claims. Xero is not counted." },
    projects: { scope: "active-only" },
    dates: { scope: "own-window", detail: PARETO_WINDOW_DETAIL },
    period: "The last N months set by Range.",
  },
  "pnl.pareto.col.share": {
    name: "% Tot",
    meaning: "This project's share of the total across every project in the table.",
    formula: ["The project's total divided by the total of all projects in the table, times 100.", "Rounded to one decimal place."],
    source: { kind: "calculated", detail: "Uses the Total column." },
    projects: { scope: "active-only" },
    dates: { scope: "own-window", detail: PARETO_WINDOW_DETAIL },
    period: "The last N months set by Range.",
  },
  "pnl.pareto.col.cumulative": {
    name: "Cum %",
    meaning: "The running share from the top of the table down to this project.",
    formula: [
      "Adds this project's total to every total above it, divided by the total of all projects, times 100.",
      "Where it passes 80%, the Vital projects end.",
    ],
    source: { kind: "calculated", detail: "Uses the Total column." },
    projects: { scope: "active-only" },
    dates: { scope: "own-window", detail: PARETO_WINDOW_DETAIL },
    period: "The last N months set by Range.",
  },
  "pnl.pareto.col.category": {
    name: "Cat (Vital or Trivial)",
    meaning: "Whether the project is one of the few that make up most of the total.",
    formula: [
      "Vital: the running share (Cum %) is 80% or less, or it is the first project.",
      "Trivial: every project after that.",
    ],
    source: { kind: "calculated", detail: "Uses the Cum % column." },
    projects: { scope: "active-only" },
    dates: { scope: "own-window", detail: PARETO_WINDOW_DETAIL },
    period: "The last N months set by Range.",
  },
} satisfies Record<string, MetricDefinition>;
